<template>
  <div class="daily-plan-container">
    <!-- 全屏加载覆盖 -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="loader-card">
        <div class="spinner"></div>
        <div class="loader-text">正在加载...</div>
      </div>
    </div>
    <!-- 顶部统计卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);">
          📋
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ tasks.length }}</div>
          <div class="stat-label">今日任务</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #FF9500 0%, #FF2D55 100%);">
          ⏱️
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ totalHours }}h</div>
          <div class="stat-label">预计工时</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #34C759 0%, #30D158 100%);">
          ✅
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ completedCount }}</div>
          <div class="stat-label">已完成</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #5AC8FA 0%, #007AFF 100%);">
          📊
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ completionRate }}%</div>
          <div class="stat-label">完成率</div>
        </div>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <h1 class="page-title">📅 每日计划</h1>
        <div class="date-badge">{{ formatDateBadge(selectedDate) }}</div>
      </div>
      <div class="toolbar-right">
        <button class="btn-apple" @click="batchDialogVisible = true">
          <span class="btn-icon">🔁</span>
          <span class="btn-text">批量重复</span>
        </button>
        <button class="btn-apple" @click="rollover">
          <span class="btn-icon">🔄</span>
          <span class="btn-text">滚动任务</span>
        </button>
      </div>
    </div>

    <!-- 日期选择器：默认单行横滑，可展开月历 -->
    <div class="cal" :class="{ 'is-expanded': calExpanded }" v-if="weekDays.length > 0">
      <!-- 单行条：上一天 / 日期胶囊横滑 / 下一天 -->
      <div class="cal-bar">
        <button
          class="cal-step"
          :disabled="!hasPrevTaskDate"
          title="上一个有任务的日期"
          @click="stepTaskDate(-1)">‹</button>

        <div class="cal-strip" ref="stripRef">
          <button
            v-for="chip in taskDateChips"
            :key="chip.date"
            class="chip"
            :class="{ 'is-active': chip.date === selectedDate, 'is-today': chip.isToday }"
            :data-date="chip.date"
            @click="selectDate(chip.date)">
            <span class="chip-date">{{ chip.label }}</span>
            <span class="chip-dow">{{ chip.dayName }}</span>
            <span class="chip-count">{{ chip.taskCount }}</span>
          </button>
        </div>

        <button
          class="cal-step"
          :disabled="!hasNextTaskDate"
          title="下一个有任务的日期"
          @click="stepTaskDate(1)">›</button>

        <div class="cal-bar-right">
          <button class="cal-today-btn" @click="goToday">今天</button>
          <button class="cal-toggle" @click="toggleCal">
            {{ calExpanded ? '收起日历 ⌃' : '展开日历 ⌄' }}
          </button>
        </div>
      </div>

      <!-- 展开后的月历，仅用于跳转任意日期 -->
      <div class="cal-panel" v-show="calExpanded">
        <div class="cal-panel-head">
          <button class="cal-nav-btn" @click="prevMonth" title="上一月">‹</button>
          <div class="cal-title">{{ viewYear }}年{{ viewMonth + 1 }}月</div>
          <button class="cal-nav-btn" @click="nextMonth" title="下一月">›</button>
          <span class="cal-summary">
            本月 {{ monthStat.days }} 天有任务 · 共 {{ monthStat.tasks }} 个
          </span>
        </div>

        <div class="cal-weekbar">
          <span v-for="w in ['一','二','三','四','五','六','日']" :key="w" class="cal-wd">{{ w }}</span>
        </div>

        <div class="cal-grid">
          <div
            v-for="cell in monthCells"
            :key="cell.key"
            class="cal-cell"
            :class="{
              'is-outside': !cell.inMonth,
              'is-active': cell.date === selectedDate,
              'is-today': cell.isToday,
              'has-task': cell.taskCount > 0,
              'is-weekend': cell.isWeekend
            }"
            @click="selectDate(cell.date)">
            <span class="cal-day">{{ cell.dayNumber }}</span>
            <span v-if="cell.taskCount > 0" class="cal-dot"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 无周计划提示 -->
    <div class="no-schedule-tip" v-else>
      <div class="tip-icon">📋</div>
      <div class="tip-text">暂无周计划，请先在任务列表中生成周计划</div>
      <button class="btn-apple-primary" @click="$router.push('/tasks')">
        <span class="btn-icon">🎯</span>
        <span class="btn-text">去生成周计划</span>
      </button>
    </div>

    <!-- 进度条 -->
    <div class="progress-section" v-if="tasks.length > 0">
      <div class="progress-header">
        <span class="progress-title">🔥 今日进度</span>
        <span class="progress-text">
          {{ completedCount }} / {{ tasks.length }} 任务完成
          <span v-if="completionRate >= 100" class="completion-badge">🎉 完美!</span>
          <span v-else-if="completionRate >= 80" class="completion-badge good">💪 加油!</span>
        </span>
      </div>
      <div class="progress-bar-container">
        <div class="progress-bar" :style="{ width: completionRate + '%' }" :class="{ 'completed': completionRate >= 100 }"></div>
      </div>
      <div class="progress-time">
        <span>🌅 10:00</span>
        <span>🍱 12:00</span>
        <span>☕ 14:00</span>
        <span>💪 16:00</span>
        <span>🍽️ 18:00</span>
        <span>🌙 20:00</span>
        <span>✨ 22:00</span>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="tasks.length === 0" class="empty-state">
      <div class="empty-icon">📭</div>
      <div class="empty-title">今日暂无任务</div>
      <div class="empty-desc">享受轻松的一天，或者去任务列表添加新任务</div>
      <button class="btn-apple-primary" @click="$router.push('/tasks')">
        <span class="btn-icon">➕</span>
        <span class="btn-text">添加任务</span>
      </button>
    </div>

    <!-- 任务列表 -->
    <div v-else class="tasks-container">
      <!-- 简洁任务列表 -->
      <div class="task-list">
        <div 
          v-for="(task, index) in tasks" 
          :key="task.id" 
          class="task-item"
          :class="{ 'completed': task.status === '已完成' }">
          
          <!-- 左侧：序号和状态 -->
          <div class="task-left">
            <div class="task-number">{{ index + 1 }}</div>
            <button 
              class="task-checkbox"
              :class="{ 'checked': task.status === '已完成' }"
              @click="toggleTaskStatus(task)">
              <span v-if="task.status === '已完成'" class="check-icon">✓</span>
            </button>
          </div>
          
          <!-- 中间：任务信息 -->
          <div class="task-main">
            <div class="task-title">{{ task.taskName }}</div>
            <div class="task-info">
              <span class="info-item">{{ task.taskType }}</span>
              <span class="info-divider">·</span>
              <span class="info-item">{{ task.requiredHours }}h</span>
              <span class="info-divider">·</span>
              <span class="info-item" :class="getDeadlineClass(task.deadline)">
                {{ formatDeadline(task.deadline) }}
              </span>
              <!-- 计时显示 -->
              <template v-if="task.status === '进行中' && task.startTime">
                <span class="info-divider">·</span>
                <span class="info-item timer-display">
                  ⏱️ {{ formatElapsedTime(task) }}
                </span>
              </template>
              <!-- 实际用时显示 -->
              <template v-if="task.status === '已完成' && task.actualSeconds !== null && task.actualSeconds !== undefined">
                <span class="info-divider">·</span>
                <span class="info-item actual-time">
                  ✅ 用时: {{ formatActualTime(task.actualSeconds) }}
                </span>
              </template>
            </div>
          </div>
          
          <!-- 右侧：重要程度标签和操作按钮 -->
          <div class="task-actions">
            <!-- 重要程度标签 -->
            <div v-if="task.importance !== '正常'" class="importance-tag" :class="'tag-' + getImportanceLevel(task.importance)">
              {{ task.importance === '重要' ? '!' : '!!' }}
            </div>
            
            <!-- 开始/完成按钮 -->
            <button 
              v-if="task.status === '待处理'"
              class="btn-timer btn-start"
              @click.stop="handleStartTask(task)">
              <span class="btn-icon">▶️</span>
              <span class="btn-text">开始</span>
            </button>
            
            <button 
              v-else-if="task.status === '进行中'"
              class="btn-timer btn-complete"
              @click.stop="handleCompleteTask(task)">
              <span class="btn-icon">✅</span>
              <span class="btn-text">完成</span>
            </button>
            
            <!-- 编排按钮 -->
            <button class="btn-apple btn-small" @click.stop="manualSchedule(task)">
              <span class="btn-icon">🛠️</span>
              <span class="btn-text">编排</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 批量添加重复任务 -->
    <BatchRepeatDialog
      v-model:visible="batchDialogVisible"
      @created="handleBatchCreated"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as taskApi from '../api/task'
import BatchRepeatDialog from '../components/BatchRepeatDialog.vue'

export default {
  name: 'DailyPlan',
  components: { BatchRepeatDialog },
  setup() {
    const tasks = ref([])
    const selectedDate = ref(new Date().toISOString().split('T')[0])
    const allScheduledTasks = ref([]) // 存储所有有周计划的任务
    const pageLoading = ref(true)
    let timerInterval = null // 计时器间隔
    
    const totalHours = computed(() => {
      return tasks.value
        .filter(t => t.status !== '已完成')
        .reduce((sum, task) => sum + (task.requiredHours || 0), 0)
        .toFixed(1)
    })

    const completedCount = computed(() => {
      return tasks.value.filter(t => t.status === '已完成').length
    })

    const completionRate = computed(() => {
      if (tasks.value.length === 0) return 0
      return Math.round((completedCount.value / tasks.value.length) * 100)
    })

    // ========== 日期选择器状态 ==========
    const batchDialogVisible = ref(false)
    const today = new Date()
    const viewYear = ref(today.getFullYear())
    const viewMonth = ref(today.getMonth()) // 0-11
    const stripRef = ref(null)

    // 月历展开状态：默认收起，记住用户选择
    const CAL_KEY = 'dailyPlan.calExpanded'
    const calExpanded = ref(localStorage.getItem(CAL_KEY) === '1')

    const toggleCal = () => {
      calExpanded.value = !calExpanded.value
      localStorage.setItem(CAL_KEY, calExpanded.value ? '1' : '0')
      // 展开时把月历定位到当前选中的日期所在月
      if (calExpanded.value) focusMonthOf(selectedDate.value)
    }

    // 日期 -> 任务数 的映射
    const taskCountByDate = computed(() => {
      const map = {}
      allScheduledTasks.value.forEach(task => {
        if (!task.scheduledDate) return
        let dateStr
        if (typeof task.scheduledDate === 'string') {
          dateStr = task.scheduledDate.split('T')[0].split(' ')[0]
        } else {
          dateStr = toLocalDateStr(new Date(task.scheduledDate))
        }
        map[dateStr] = (map[dateStr] || 0) + 1
      })
      return map
    })

    // 本地时区的 yyyy-MM-dd，避免 toISOString 的 UTC 偏移导致日期错位
    const toLocalDateStr = (d) => {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }

    // 当月网格：周一为每周起始，补齐前后空位，固定 6 行 42 格
    const monthCells = computed(() => {
      const cells = []
      const todayStr = toLocalDateStr(new Date())
      const first = new Date(viewYear.value, viewMonth.value, 1)

      // JS getDay: 0=周日，转成以周一为 0 的偏移
      const leading = (first.getDay() + 6) % 7

      // 从网格第一格对应的日期开始，连续铺 42 天
      const gridStart = new Date(viewYear.value, viewMonth.value, 1 - leading)

      for (let i = 0; i < 42; i++) {
        const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)
        const dateStr = toLocalDateStr(d)
        const dow = d.getDay()
        cells.push({
          key: dateStr,
          date: dateStr,
          dayNumber: d.getDate(),
          inMonth: d.getMonth() === viewMonth.value,
          isToday: dateStr === todayStr,
          isWeekend: dow === 0 || dow === 6,
          taskCount: taskCountByDate.value[dateStr] || 0
        })
      }
      return cells
    })

    // 当月统计
    const monthStat = computed(() => {
      let days = 0
      let tasks = 0
      monthCells.value.forEach(c => {
        if (c.inMonth && c.taskCount > 0) {
          days++
          tasks += c.taskCount
        }
      })
      return { days, tasks }
    })

    const prevMonth = () => {
      if (viewMonth.value === 0) {
        viewMonth.value = 11
        viewYear.value--
      } else {
        viewMonth.value--
      }
    }

    const nextMonth = () => {
      if (viewMonth.value === 11) {
        viewMonth.value = 0
        viewYear.value++
      } else {
        viewMonth.value++
      }
    }

    const goToday = () => {
      const now = new Date()
      viewYear.value = now.getFullYear()
      viewMonth.value = now.getMonth()
      selectDate(toLocalDateStr(now))
    }

    // 把月历视图切到指定日期所在的月份
    const focusMonthOf = (dateStr) => {
      if (!dateStr) return
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return
      viewYear.value = d.getFullYear()
      viewMonth.value = d.getMonth()
    }

    // 批量创建成功后刷新日历和当天任务
    const handleBatchCreated = async () => {
      await loadScheduledTasks()
      await loadDailyTasks()
    }

    // 所有有任务的日期（升序）。仅用于判断是否已有排期、以及默认选中
    const weekDays = computed(() =>
      Object.keys(taskCountByDate.value)
        .sort()
        .map(dateStr => ({ date: dateStr, taskCount: taskCountByDate.value[dateStr] }))
    )

    // 单行横滑用的日期胶囊：只放有任务的日期，不铺空白
    const taskDateChips = computed(() => {
      const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const todayStr = toLocalDateStr(new Date())
      return weekDays.value.map(d => {
        const dt = new Date(d.date)
        return {
          date: d.date,
          label: `${dt.getMonth() + 1}/${dt.getDate()}`,
          dayName: dayNames[dt.getDay()],
          taskCount: d.taskCount,
          isToday: d.date === todayStr
        }
      })
    })

    // 在「有任务的日期」之间前后跳，跳过没任务的空日子
    const selectedChipIndex = computed(() =>
      taskDateChips.value.findIndex(c => c.date === selectedDate.value)
    )
    const hasPrevTaskDate = computed(() => selectedChipIndex.value > 0)
    const hasNextTaskDate = computed(() =>
      selectedChipIndex.value > -1 && selectedChipIndex.value < taskDateChips.value.length - 1
    )

    const stepTaskDate = (delta) => {
      const list = taskDateChips.value
      if (!list.length) return
      let idx = selectedChipIndex.value
      if (idx === -1) {
        // 当前选中日没有任务，就近找一个
        const todayStr = selectedDate.value
        idx = list.findIndex(c => c.date >= todayStr)
        if (idx === -1) idx = list.length - 1
      } else {
        idx += delta
      }
      if (idx < 0 || idx >= list.length) return
      selectDate(list[idx].date)
    }

    // 让选中的胶囊滚到可见区域中间
    const scrollChipIntoView = () => {
      nextTick(() => {
        const box = stripRef.value
        if (!box) return
        const el = box.querySelector(`[data-date="${selectedDate.value}"]`)
        if (!el) return
        box.scrollTo({
          left: el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2,
          behavior: 'smooth'
        })
      })
    }

    // 选择日期
    const selectDate = async (date) => {
      if (!date) return
      selectedDate.value = date
      scrollChipIntoView()
      await nextTick()
      await loadDailyTasks()
    }

    // 加载所有有周计划的任务
    const loadScheduledTasks = async () => {
      try {
        // 获取所有任务（分页获取大量数据）
        const res = await taskApi.getTasksByPage(1, 1000)
        if (res.data.code === 200) {
          // 只保留有 scheduledDate 的任务
          allScheduledTasks.value = res.data.data.records.filter(task => task.scheduledDate)
          
          // 如果有任务，默认选择第一个日期
          if (weekDays.value.length > 0 && !selectedDate.value) {
            selectedDate.value = weekDays.value[0].date
          }

          // 月历定位：今天没任务时，跳到最近一个有任务的月份，避免打开就是空月
          if (weekDays.value.length > 0) {
            const todayStr = toLocalDateStr(new Date())
            if (!taskCountByDate.value[todayStr]) {
              const next = weekDays.value.find(d => d.date >= todayStr)
              focusMonthOf(next ? next.date : weekDays.value[weekDays.value.length - 1].date)
            }
            scrollChipIntoView()
          }
        }
      } catch (error) {
        console.error('加载周计划任务失败', error)
      }
    }

    // 时间段定义
    const timePeriods = [
      { 
        name: '上午热身', 
        time: '10:00-11:00', 
        icon: '🌅', 
        desc: '简单任务，快速进入状态',
        tagType: 'success',
        range: [10, 19]
      },
      { 
        name: '上午高峰', 
        time: '11:00-12:00', 
        icon: '🔥', 
        desc: '精力最佳，处理难题',
        tagType: 'danger',
        range: [20, 29]
      },
      { 
        name: '午休时间', 
        time: '12:00-14:00', 
        icon: '🍱', 
        desc: '休息充电',
        tagType: 'info',
        range: [-1, -1]
      },
      { 
        name: '下午恢复', 
        time: '14:00-15:00', 
        icon: '☕', 
        desc: '简单任务，恢复状态',
        tagType: 'success',
        range: [30, 39]
      },
      { 
        name: '下午高峰', 
        time: '15:00-18:00', 
        icon: '💪', 
        desc: '第二高峰，中等任务',
        tagType: 'warning',
        range: [40, 49]
      },
      { 
        name: '晚餐休息', 
        time: '18:00-20:00', 
        icon: '🍽️', 
        desc: '休息放松',
        tagType: 'info',
        range: [-1, -1]
      },
      { 
        name: '晚上收尾', 
        time: '20:00-22:00', 
        icon: '🌙', 
        desc: '简单任务，轻松结束',
        tagType: 'success',
        range: [50, 59]
      }
    ]

    // 根据时间段筛选任务
    const getTasksInPeriod = (period) => {
      if (period.range[0] === -1) {
        return [] // 休息时间不显示任务
      }
      return tasks.value.filter((task, index) => {
        // 根据任务在列表中的位置判断属于哪个时间段
        // 后端已经按最佳顺序排序，这里根据排序分数范围分组
        const score = task.priorityScore || (index * 10)
        return score >= period.range[0] && score <= period.range[1]
      })
    }

    const loadDailyTasks = async () => {
      pageLoading.value = true
      try {
        console.log('加载每日任务，日期：', selectedDate.value)
        const res = await taskApi.getDailyTasks(selectedDate.value)
        console.log('API响应：', res.data)
        if (res.data.code === 200) {
          tasks.value = res.data.data
          console.log('任务数量：', tasks.value.length)
          // 打印每个任务的状态和startTime
          tasks.value.forEach(task => {
            console.log(`任务[${task.taskName}] 状态:${task.status}, startTime:${task.startTime}, actualSeconds:${task.actualSeconds}`)
            // 如果是已完成任务，打印更详细的信息
            if (task.status === '已完成') {
              console.log(`  -> 已完成任务详情:`, {
                actualSeconds: task.actualSeconds,
                actualSecondsType: typeof task.actualSeconds,
                hasActualSeconds: !!task.actualSeconds
              })
            }
          })
          // 加载完任务后检查是否需要启动计时器
          checkAndStartTimer()
        }
      } catch (error) {
        console.error('加载任务失败：', error)
        ElMessage.error('加载任务失败')
      } finally {
        pageLoading.value = false
      }
    }

    const updateStatus = async (task, status) => {
      // 先更新本地状态，实现即时响应
      const taskIndex = tasks.value.findIndex(t => t.id === task.id)
      if (taskIndex !== -1) {
        tasks.value[taskIndex].status = status
        if (status === '已完成') {
          tasks.value[taskIndex].completedDate = new Date()
        }
      }
      
      try {
        const res = await taskApi.updateTaskStatus(task.id, status)
        if (res.data.code === 200) {
          ElMessage.success('状态更新成功')
          // 重新加载以确保数据同步
          await loadDailyTasks()
        }
      } catch (error) {
        ElMessage.error('更新失败')
        // 如果失败，重新加载恢复正确状态
        await loadDailyTasks()
      }
    }

    const rollover = async () => {
      try {
        const res = await taskApi.rolloverTasks()
        if (res.data.code === 200) {
          ElMessage.success(res.data.data || '操作成功')
          await loadDailyTasks()
          await loadScheduledTasks() // 刷新周计划
        } else {
          ElMessage.info(res.data.message || '无需滚动')
        }
      } catch (error) {
        ElMessage.error('操作失败')
      }
    }

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }

    const formatDeadline = (date) => {
      if (!date) return ''
      const d = new Date(date)
      const now = new Date()
      const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) return '已过期'
      if (diffDays === 0) return '今天'
      if (diffDays === 1) return '明天'
      return `${diffDays}天后`
    }

    const getStatusType = (status) => {
      const map = { '待处理': 'info', '进行中': 'warning', '已完成': 'success' }
      return map[status] || 'info'
    }

    const getImportanceType = (importance) => {
      const map = { '正常': '', '重要': 'warning', '非常重要': 'danger' }
      return map[importance] || ''
    }

    const getImportanceLevel = (importance) => {
      const map = { '正常': 'normal', '重要': 'important', '非常重要': 'critical' }
      return map[importance] || 'normal'
    }

    const getStatusClass = (status) => {
      const map = { '待处理': 'status-pending', '进行中': 'status-progress', '已完成': 'status-done' }
      return map[status] || 'status-pending'
    }

    const getDeadlineClass = (deadline) => {
      const d = new Date(deadline)
      const now = new Date()
      const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) return 'deadline-overdue'
      if (diffDays === 0) return 'deadline-today'
      if (diffDays === 1) return 'deadline-tomorrow'
      return 'deadline-normal'
    }

    const formatDateBadge = (date) => {
      const d = new Date(date)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      if (d.toDateString() === today.toDateString()) {
        return '今天'
      } else if (d.toDateString() === tomorrow.toDateString()) {
        return '明天'
      } else {
        const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
        return `${d.getMonth() + 1}月${d.getDate()}日 ${weekdays[d.getDay()]}`
      }
    }

    // 切换任务状态
    const toggleTaskStatus = async (task) => {
      let newStatus = '已完成'
      if (task.status === '已完成') {
        newStatus = '待处理'
      } else if (task.status === '待处理') {
        newStatus = '已完成'
      } else {
        newStatus = '已完成'
      }
      
      await updateStatus(task, newStatus)
    }

    const manualSchedule = async (task) => {
      try {
        const d = new Date(selectedDate.value)
        d.setDate(d.getDate() + 1)
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        const nextDate = `${y}-${m}-${day}`
        const res = await taskApi.scheduleTask(task.id, nextDate)
        if (res.data.code === 200) {
          ElMessage.success('已编排到下一天')
          await loadScheduledTasks()
          await loadDailyTasks()
        } else {
          ElMessage.error(res.data.message || '编排失败')
        }
      } catch (error) {
        ElMessage.error('编排失败')
      }
    }

    // 开始任务计时
    const handleStartTask = async (task) => {
      try {
        console.log('开始任务:', task.taskName, '任务ID:', task.id)
        const res = await taskApi.startTask(task.id)
        console.log('开始任务响应:', res.data)
        if (res.data.code === 200) {
          ElMessage.success('任务已开始计时')
          // 重新加载任务数据
          await loadDailyTasks()
        } else {
          ElMessage.error(res.data.message || '开始失败')
        }
      } catch (error) {
        console.error('开始任务失败:', error)
        ElMessage.error('开始失败')
      }
    }

    // 完成任务
    const handleCompleteTask = async (task) => {
      try {
        console.log('完成任务:', task.taskName, '任务ID:', task.id)
        const res = await taskApi.completeTask(task.id)
        console.log('完成任务响应:', res.data)
        if (res.data.code === 200) {
          const elapsedTime = formatElapsedTime(task)
          ElMessage.success(`任务已完成！实际用时: ${elapsedTime}`)
          // 重新加载任务数据
          await loadDailyTasks()
          await loadScheduledTasks()
        } else {
          ElMessage.error(res.data.message || '完成失败')
        }
      } catch (error) {
        console.error('完成任务失败:', error)
        ElMessage.error('完成失败')
      }
    }

    // 格式化已经过的时间（实时计时显示）
    const formatElapsedTime = (task) => {
      if (!task.startTime) {
        console.log('任务没有startTime:', task.taskName)
        return '0秒'
      }
      
      const startTime = new Date(task.startTime)
      const now = new Date()
      const diffMs = now - startTime
      const totalSeconds = Math.floor(diffMs / 1000)
      
      // 使用统一的格式化函数
      return formatActualTime(totalSeconds)
    }

    // 格式化实际用时
    const formatActualHours = (actualHours) => {
      if (!actualHours && actualHours !== 0) return '0.00'
      // 处理BigDecimal类型，可能是数字或对象
      const hours = typeof actualHours === 'number' ? actualHours : parseFloat(actualHours)
      return hours.toFixed(2)
    }

    // 格式化实际用时（更友好的显示格式）
    const formatActualTime = (actualSeconds) => {
      if (actualSeconds === null || actualSeconds === undefined) return '0秒'
      
      const seconds = typeof actualSeconds === 'number' ? actualSeconds : parseInt(actualSeconds)
      
      // 如果小于1分钟，显示秒
      if (seconds < 60) {
        return `${seconds}秒`
      }
      
      // 如果小于1小时，显示分钟和秒
      if (seconds < 3600) {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}分${secs}秒`
      }
      
      // 超过1小时，显示小时和分钟
      const hours = Math.floor(seconds / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      return `${hours}小时${mins}分`
    }

    // 启动计时器（每秒更新界面）
    const startTimer = () => {
      if (timerInterval) {
        console.log('计时器已经在运行')
        return // 已经有计时器在运行
      }
      
      console.log('启动计时器')
      timerInterval = setInterval(() => {
        // 强制更新界面，显示计时器
        const runningTask = tasks.value.find(t => t.status === '进行中' && t.startTime)
        if (runningTask) {
          console.log('计时器更新:', runningTask.taskName, formatElapsedTime(runningTask))
        }
        tasks.value = [...tasks.value]
      }, 1000)
    }

    // 停止计时器
    const stopTimer = () => {
      if (timerInterval) {
        console.log('停止计时器')
        clearInterval(timerInterval)
        timerInterval = null
      }
    }

    // 检查是否有进行中的任务，如果有则启动计时器
    const checkAndStartTimer = () => {
      const hasRunningTask = tasks.value.some(t => t.status === '进行中' && t.startTime)
      console.log('检查进行中的任务:', hasRunningTask)
      if (hasRunningTask) {
        const runningTask = tasks.value.find(t => t.status === '进行中' && t.startTime)
        console.log('进行中的任务:', runningTask?.taskName, '开始时间:', runningTask?.startTime)
        startTimer()
      } else {
        stopTimer()
      }
    }

    onMounted(() => {
      loadScheduledTasks()
      loadDailyTasks()
    })

    onUnmounted(() => {
      stopTimer() // 组件销毁时清理计时器
    })

    return {
      tasks,
      selectedDate,
      totalHours,
      completedCount,
      completionRate,
      timePeriods,
      weekDays,
      batchDialogVisible,
      viewYear,
      viewMonth,
      monthCells,
      monthStat,
      prevMonth,
      nextMonth,
      goToday,
      handleBatchCreated,
      stripRef,
      calExpanded,
      toggleCal,
      taskDateChips,
      hasPrevTaskDate,
      hasNextTaskDate,
      stepTaskDate,
      selectDate,
      toggleTaskStatus,
      loadDailyTasks,
      updateStatus,
      rollover,
      manualSchedule,
      formatDate,
      formatDeadline,
      formatDateBadge,
      getStatusType,
      getImportanceType,
      getImportanceLevel,
      getStatusClass,
      getDeadlineClass,
      getTasksInPeriod,
      pageLoading,
      handleStartTask,
      handleCompleteTask,
      formatElapsedTime,
      formatActualHours,
      formatActualTime
    }
  }
}
</script>

<style scoped>
/* 全屏加载覆盖样式（苹果风格） */
.page-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(242, 242, 247, 0.8);
  backdrop-filter: saturate(180%) blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
.loader-card {
  background: white;
  border: 1px solid #e5e5ea;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  gap: 12px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e5e5ea;
  border-top-color: #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loader-text { font-weight: 700; color: #1d1d1f; }
@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

/* ========== 容器和布局 ========== */
.daily-plan-container {
  position: relative;
  min-height: 100vh;
  background: #F2F2F7;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

/* ========== 统计卡片 ========== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #1d1d1f;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #86868b;
  font-weight: 500;
}

/* ========== 工具栏 ========== */
.toolbar {
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
  gap: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.date-badge {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
}

.toolbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* ========== 日期选择器（默认单行，可展开月历） ========== */
.cal {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  padding: 8px 10px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* --- 单行条 --- */
.cal-bar {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cal-step {
  flex: 0 0 auto;
  width: 28px;
  height: 34px;
  border: none;
  background: transparent;
  color: #6e6e73;
  border-radius: 9px;
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.18s;
}

.cal-step:hover:not(:disabled) {
  background: #F0F0F3;
  color: #1d1d1f;
}

.cal-step:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

/* 横向滑动的日期胶囊条：只占一行 */
.cal-strip {
  flex: 1 1 auto;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 2px 0;
  scrollbar-width: thin;
}

.cal-strip::-webkit-scrollbar {
  height: 4px;
}

.cal-strip::-webkit-scrollbar-thumb {
  background: #D8D8DD;
  border-radius: 2px;
}

.cal-strip::-webkit-scrollbar-track {
  background: transparent;
}

.chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  height: 34px;
  padding: 0 11px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #F7F7F9;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.chip:hover {
  background: #EFEFF2;
}

.chip-date {
  font-size: 13px;
  font-weight: 650;
  color: #1d1d1f;
  letter-spacing: -0.2px;
}

.chip-dow {
  font-size: 11px;
  color: #8e8e93;
  font-weight: 500;
}

.chip-count {
  align-self: center;
  font-size: 10px;
  font-weight: 700;
  color: #007AFF;
  background: #E5EFFF;
  border-radius: 999px;
  min-width: 17px;
  text-align: center;
  padding: 2px 5px;
  line-height: 1;
}

/* 今天：蓝色描边 + 淡蓝底 */
.chip.is-today {
  border-color: rgba(0, 122, 255, 0.35);
  background: #F5FAFF;
}

/* 选中：纯色填充，比渐变干净 */
.chip.is-active {
  background: #007AFF;
  border-color: #007AFF;
  box-shadow: 0 2px 6px rgba(0, 122, 255, 0.25);
}

.chip.is-active .chip-date {
  color: white;
}

.chip.is-active .chip-dow {
  color: rgba(255, 255, 255, 0.8);
}

.chip.is-active .chip-count {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.cal-bar-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.cal-today-btn,
.cal-toggle {
  border: none;
  font-size: 12px;
  font-weight: 600;
  height: 34px;
  padding: 0 12px;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.cal-today-btn {
  background: #EBF4FF;
  color: #007AFF;
}

.cal-today-btn:hover {
  background: #DBEAFF;
}

.cal-toggle {
  background: transparent;
  color: #6e6e73;
}

.cal-toggle:hover {
  background: #F0F0F3;
  color: #1d1d1f;
}

/* --- 展开的月历面板 --- */
.cal-panel {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #F0F0F3;
}

.cal-panel-head {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.cal-nav-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6e6e73;
  border-radius: 7px;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.18s;
}

.cal-nav-btn:hover {
  background: #F0F0F3;
  color: #1d1d1f;
}

.cal-title {
  font-size: 13px;
  font-weight: 650;
  color: #1d1d1f;
  min-width: 92px;
  text-align: center;
  letter-spacing: -0.2px;
}

.cal-summary {
  margin-left: auto;
  font-size: 11px;
  color: #a1a1a6;
}

/* 星期表头 */
.cal-weekbar {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding-bottom: 6px;
  margin-bottom: 4px;
  border-bottom: 1px solid #F5F5F7;
}

.cal-wd {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #a1a1a6;
}

/* 紧凑格子：固定 32px 行高，整月 6 行约 210px */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}

.cal-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 9px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}

.cal-cell:hover {
  background: #F2F2F5;
}

.cal-day {
  font-size: 12.5px;
  font-weight: 550;
  color: #1d1d1f;
  line-height: 1;
}

/* 有任务的日子：底部小圆点，不占高度 */
.cal-dot {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #007AFF;
}

.cal-cell.has-task {
  background: #F5FAFF;
}

.cal-cell.has-task .cal-day {
  font-weight: 700;
  color: #0066DB;
}

.cal-cell.has-task:hover {
  background: #E8F3FF;
}

/* 周末弱化 */
.cal-cell.is-weekend .cal-day {
  color: #a1a1a6;
}

/* 非本月 */
.cal-cell.is-outside .cal-day {
  color: #d8d8dd;
  font-weight: 500;
}

.cal-cell.is-outside.has-task {
  background: transparent;
}

.cal-cell.is-outside .cal-dot {
  opacity: 0.35;
}

/* 今天：描边 */
.cal-cell.is-today {
  border-color: rgba(0, 122, 255, 0.45);
}

/* 选中：实心 */
.cal-cell.is-active {
  background: #007AFF;
  border-color: #007AFF;
}

.cal-cell.is-active:hover {
  background: #0071EB;
}

.cal-cell.is-active .cal-day {
  color: white;
  font-weight: 700;
}

.cal-cell.is-active .cal-dot {
  background: white;
}

.day-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}

.task-count {
  font-size: 12px;
  color: #007AFF;
  font-weight: 600;
  background: rgba(0, 122, 255, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
}

.cal-cell.is-active .task-count {
  background: rgba(255, 255, 255, 0.2);
}

/* 无周计划提示 */
.no-schedule-tip {
  background: white;
  border-radius: 20px;
  padding: 48px 24px;
  margin-bottom: 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.no-schedule-tip .tip-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.no-schedule-tip .tip-text {
  font-size: 16px;
  color: #86868b;
  margin-bottom: 24px;
}

.btn-apple {
  background: white;
  border: 2px solid #e5e5e7;
  border-radius: 12px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-apple:hover {
  background: #f5f5f7;
  border-color: #d2d2d7;
}

.btn-apple-primary {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.btn-apple-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
}

.btn-icon {
  font-size: 16px;
}

.btn-text {
  font-size: 14px;
}

/* ========== 进度条 ========== */
.progress-section {
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.progress-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
}

.progress-text {
  font-size: 14px;
  color: #86868b;
}

.progress-bar-container {
  height: 16px;
  background: #f5f5f7;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #007AFF 0%, #5856D6 50%, #AF52DE 100%);
  border-radius: 8px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(0, 122, 255, 0.4);
  position: relative;
  overflow: hidden;
}

/* 流光效果 */
.progress-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* 进度条脉冲光晕 */
.progress-bar-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 8px;
  box-shadow: inset 0 0 8px rgba(0, 122, 255, 0.1);
  pointer-events: none;
}

.progress-time {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #86868b;
  margin-top: 8px;
}

/* 完成徽章 */
.completion-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: white;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  animation: bounce 0.6s ease infinite;
}

.completion-badge.good {
  background: linear-gradient(135deg, #34C759 0%, #30D158 100%);
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

/* 进度条完成状态 */
.progress-bar.completed {
  background: linear-gradient(90deg, #34C759 0%, #30D158 50%, #5AC8FA 100%);
  animation: celebrate 1s ease-in-out infinite;
}

@keyframes celebrate {
  0%, 100% { box-shadow: 0 2px 12px rgba(52, 199, 89, 0.4); }
  50% { box-shadow: 0 4px 20px rgba(52, 199, 89, 0.6); }
}

/* 完成状态不显示::after伪元素，用.runner.winner代替 */
.progress-bar.completed::after {
  display: none;
}

/* ========== 空状态 ========== */
.empty-state {
  background: white;
  border-radius: 20px;
  padding: 60px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 16px;
  color: #86868b;
  margin-bottom: 24px;
}

/* ========== 任务容器 ========== */
.tasks-container {
  background: white;
  border-radius: 20px;
  padding: 0;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

/* ========== 简约任务列表 ========== */
.task-list {
  padding: 0;
}

.task-item {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #F2F2F7;
  transition: all 0.2s;
  cursor: pointer;
}

.task-item:last-child {
  border-bottom: none;
}

.task-item:hover {
  background: #FAFAFA;
}

.task-item.completed {
  opacity: 0.5;
}

.task-item.completed .task-title {
  text-decoration: line-through;
  color: #86868b;
}

/* 左侧：序号和复选框 */
.task-left {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: 20px;
}

.task-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #F2F2F7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #86868b;
}

.task-checkbox {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #D1D1D6;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.task-checkbox:hover {
  border-color: #007AFF;
  transform: scale(1.1);
}

.task-checkbox.checked {
  background: #007AFF;
  border-color: #007AFF;
}

.check-icon {
  color: white;
  font-size: 14px;
  font-weight: bold;
}

/* 中间：任务信息 */
.task-main {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-size: 16px;
  font-weight: 500;
  color: #1D1D1F;
  margin-bottom: 6px;
  line-height: 1.4;
}

.task-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #86868b;
}

.info-item {
  white-space: nowrap;
}

.info-divider {
  color: #D1D1D6;
}

.deadline-overdue {
  color: #FF3B30;
  font-weight: 600;
}

.deadline-today {
  color: #FF9500;
  font-weight: 600;
}

.deadline-tomorrow {
  color: #007AFF;
}

/* 右侧：重要程度标签 */
.task-right {
  margin-left: 16px;
}

/* 任务操作区域 */
.task-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
}

.importance-tag {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
}

.tag-important {
  background: #FFF3CD;
  color: #FF9500;
}

.tag-critical {
  background: #FFE5E5;
  color: #FF3B30;
}

/* 计时按钮样式 */
.btn-timer {
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-start {
  background: linear-gradient(135deg, #34C759 0%, #30D158 100%);
  color: white;
}

.btn-start:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(52, 199, 89, 0.3);
}

.btn-complete {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
}

.btn-complete:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

/* 计时显示 */
.timer-display {
  color: #007AFF;
  font-weight: 600;
  font-family: 'Monaco', 'Courier New', monospace;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* 实际用时显示 */
.actual-time {
  color: #34C759;
  font-weight: 600;
}



/* ========== 响应式布局 ========== */
@media (max-width: 768px) {
  .daily-plan-container {
    padding: 12px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    font-size: 24px;
  }

  .stat-value {
    font-size: 24px;
  }

  .toolbar {
    padding: 16px;
  }

  .toolbar-left {
    width: 100%;
  }

  .toolbar-right {
    width: 100%;
    justify-content: flex-end;
  }

  .page-title {
    font-size: 22px;
  }

  /* 日期选择器响应式：窄屏把"今天/展开"挪到第二行 */
  .cal {
    padding: 8px 10px;
  }

  .cal-bar {
    flex-wrap: wrap;
  }

  .cal-strip {
    order: 2;
    width: 100%;
    flex-basis: 100%;
  }

  .cal-step {
    order: 1;
  }

  .cal-bar-right {
    order: 1;
    margin-left: auto;
  }

  .cal-title {
    font-size: 13px;
    min-width: 88px;
  }

  .cal-summary {
    font-size: 11px;
  }

  .cal-cell {
    height: 30px;
    border-radius: 8px;
  }

  .cal-day {
    font-size: 12px;
  }

  .no-schedule-tip {
    padding: 32px 16px;
  }

  .no-schedule-tip .tip-icon {
    font-size: 48px;
  }

  .no-schedule-tip .tip-text {
    font-size: 14px;
  }

  .progress-time {
    font-size: 10px;
  }

  /* 任务列表响应式 */
  .task-item {
    padding: 16px;
  }

  .task-left {
    gap: 12px;
    margin-right: 12px;
  }

  .task-number {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }

  .task-checkbox {
    width: 22px;
    height: 22px;
  }

  .task-title {
    font-size: 15px;
  }

  .task-info {
    font-size: 12px;
    flex-wrap: wrap;
  }

  .importance-tag {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card {
    padding: 12px;
  }

  .btn-text {
    display: none;
  }

  /* 超小屏幕任务列表 */
  .task-item {
    padding: 12px;
  }

  .task-left {
    gap: 8px;
    margin-right: 8px;
  }

  .task-number {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  .task-checkbox {
    width: 20px;
    height: 20px;
  }

  .check-icon {
    font-size: 12px;
  }

  .task-title {
    font-size: 14px;
  }

  .task-info {
    font-size: 11px;
  }

  .task-right {
    margin-left: 8px;
  }

  .importance-tag {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  /* 超小屏幕日期选择器优化 */
  .cal {
    padding: 8px;
    border-radius: 12px;
  }

  .chip {
    height: 32px;
    padding: 0 9px;
    gap: 4px;
  }

  .chip-date {
    font-size: 12px;
  }

  .chip-dow {
    font-size: 10px;
  }

  .cal-step {
    height: 32px;
    width: 24px;
  }

  .cal-today-btn,
  .cal-toggle {
    height: 32px;
    font-size: 11px;
    padding: 0 9px;
  }

  .cal-cell {
    height: 28px;
    border-radius: 7px;
  }

  .cal-wd {
    font-size: 10px;
  }

  .day-indicator {
    margin-top: 6px;
  }

  .task-count {
    font-size: 10px;
    padding: 3px 6px;
  }

  .no-schedule-tip {
    padding: 24px 12px;
  }

  .no-schedule-tip .tip-icon {
    font-size: 40px;
  }

  .no-schedule-tip .tip-text {
    font-size: 13px;
  }
}
</style>
